import { apiFetch } from '@carouselgg/auth/client'

export interface CreateOrgRequest {
  name: string
  slug: string
  description: string
  type: 'organization' | 'league' | 'university'
  primary_color: string
  secondary_color: string
  accent_color: string
  social_links: Record<string, string>
  university_name?: string
  conference?: string
  recruiting_open?: boolean
  registration_open?: boolean
  discord_guild_id?: string
  discord_guild_name?: string
  discord_guild_icon?: string
  short_code?: string
}

export interface OrgResponse {
  id: string
  name: string
  slug: string
  description: string
  type: 'organization' | 'league' | 'university'
  primary_color: string
  secondary_color: string
  accent_color: string
  logo_url: string
  banner_url: string
  social_links: Record<string, string>
  university_name: string | null
  conference: string | null
  recruiting_open: boolean
  registration_open: boolean
  discord_guild_id: string | null
  discord_guild_name: string | null
  discord_guild_icon: string | null
  short_code: string | null
  status: string
  rejection_reason?: string | null
  created_at: string
}

export interface MembershipResponse {
  role: string
  joined_at: string
}

export interface CreateOrgResponse {
  organization: OrgResponse
  membership: MembershipResponse
}

export interface MyOrgItem {
  id: string
  name: string
  slug: string
  type: string
  logo_url: string
  primary_color: string
  role: string
  joined_at: string
}

export interface MyOrgsResponse {
  organizations: MyOrgItem[]
  count: number
  max: number
}

export function createOrganization(data: CreateOrgRequest): Promise<CreateOrgResponse> {
  return apiFetch<CreateOrgResponse>('/organizations', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export interface AvailabilityResult {
  available: boolean
  reason?: string
}

export function checkSlugAvailable(slug: string): Promise<AvailabilityResult> {
  return apiFetch<AvailabilityResult>(`/organizations/check-slug?slug=${encodeURIComponent(slug)}`)
}

export function checkNameAvailable(name: string): Promise<AvailabilityResult> {
  return apiFetch<AvailabilityResult>(`/organizations/check-name?name=${encodeURIComponent(name)}`)
}

export function getMyOrganizations(): Promise<MyOrgsResponse> {
  return apiFetch<MyOrgsResponse>('/organizations/mine')
}

/** Leave an organization (also removes the user from all its teams). */
export function leaveOrganization(slug: string): Promise<{ status: string }> {
  return apiFetch<{ status: string }>(`/organizations/by-slug/${encodeURIComponent(slug)}/membership`, {
    method: 'DELETE',
  })
}

/** Leave a single team within an organization. */
export function leaveTeam(slug: string, teamId: string): Promise<{ status: string }> {
  return apiFetch<{ status: string }>(
    `/organizations/by-slug/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamId)}/membership`,
    { method: 'DELETE' },
  )
}

export interface SearchOrgItem {
  id: string
  name: string
  slug: string
  description: string
  type: string
  logo_url: string
  primary_color: string
}

export async function searchOrganizations(query: string): Promise<SearchOrgItem[]> {
  const res = await apiFetch<{ organizations: SearchOrgItem[] }>(
    `/organizations/search?q=${encodeURIComponent(query)}`,
  )
  return res.organizations
}

export interface DiscordGuild {
  id: string
  name: string
  icon_url: string
  owner: boolean
  already_linked: boolean
}

export async function getDiscordGuilds(): Promise<DiscordGuild[]> {
  const res = await apiFetch<{ guilds: DiscordGuild[] }>('/organizations/discord-guilds')
  return res.guilds
}

/** How a player can reach the org running a competition — only what the org published. */
export interface OrganizerContact {
  name: string
  email?: string
  discordUrl?: string
}

export async function getOrganizerContact(slug: string): Promise<OrganizerContact> {
  const org = await apiFetch<Pick<OrgResponse, 'name' | 'social_links'> & { public_contact_email?: string }>(
    `/organizations/by-slug/${encodeURIComponent(slug)}`,
  )
  return {
    name: org.name,
    email: org.public_contact_email?.trim() || undefined,
    discordUrl: org.social_links?.discord?.trim() || undefined,
  }
}
